/**
 * Sparklet Command
 * View and manage sparklets (AI-generated micro-apps)
 */

import { Command } from 'commander';
import open from 'open';
import { getApiClient } from '../api/client.js';
import { isAuthenticated, getWebUrl } from '../utils/config.js';
import * as output from '../utils/output.js';

interface Sparklet {
    hashKey: string;
    title: string;
    description?: string;
    isPublished: boolean;
    createdAt: string;
    updatedAt: string;
}

function requireAuth(): void {
    if (!isAuthenticated()) {
        output.error('Not logged in. Run `paean login` first.');
        process.exit(1);
    }
}

export const sparkletCommand = new Command('sparklet')
    .description('View and manage sparklets (AI-generated micro-apps)')
    .action(async () => {
        requireAuth();
        await listSparklets();
    });

// Subcommand: sparklet list
sparkletCommand
    .command('list')
    .description('List your sparklets')
    .option('--json', 'Output as JSON')
    .action(async (options) => {
        requireAuth();
        await listSparklets(options.json);
    });

// Subcommand: sparklet show
sparkletCommand
    .command('show <id>')
    .description('Show sparklet details')
    .option('--json', 'Output as JSON')
    .action(async (id: string, options) => {
        requireAuth();

        try {
            const client = getApiClient();
            const response = await client.get(`/sparklet/${id}`);
            const sparklet = response.data.data || response.data;

            if (options.json) {
                output.json(sparklet);
                return;
            }

            const published = sparklet.isPublished
                ? output.colors.success('[published]')
                : output.colors.dim('[draft]');

            output.header(`${sparklet.title || 'Untitled'} ${published}`);
            output.tableRow('ID', sparklet.hashKey);
            if (sparklet.description) {
                output.tableRow('Description', sparklet.description);
            }
            output.tableRow('Created', new Date(sparklet.createdAt).toLocaleString());
            output.tableRow('Updated', new Date(sparklet.updatedAt).toLocaleString());
            output.newline();

            const webUrl = getWebUrl();
            output.dim(`View: ${webUrl}/sparklet/${sparklet.hashKey}`);
            output.newline();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            output.errorWithHint(`Failed to fetch sparklet: ${errorMessage}`);
            process.exit(1);
        }
    });

// Subcommand: sparklet open
sparkletCommand
    .command('open <id>')
    .description('Open sparklet in browser')
    .action(async (id: string) => {
        requireAuth();

        const webUrl = getWebUrl();
        const url = `${webUrl}/sparklet/${id}`;
        output.dim(`Opening ${url}...`);

        try {
            await open(url);
        } catch {
            output.dim(`Open manually: ${url}`);
        }
    });

// Subcommand: sparklet delete
sparkletCommand
    .command('delete <id>')
    .description('Delete a sparklet')
    .action(async (id: string) => {
        requireAuth();

        try {
            const client = getApiClient();
            await client.delete(`/sparklet/${id}`);
            output.success('Sparklet deleted.');
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            output.errorWithHint(`Failed to delete sparklet: ${errorMessage}`);
            process.exit(1);
        }
    });

// Subcommand: sparklet publish
sparkletCommand
    .command('publish <id>')
    .description('Publish or unpublish a sparklet')
    .option('--unpublish', 'Unpublish instead of publish')
    .action(async (id: string, options) => {
        requireAuth();

        try {
            const client = getApiClient();
            await client.put(`/sparklet/${id}/publish`, {
                isPublished: !options.unpublish,
            });
            output.success(options.unpublish ? 'Sparklet unpublished.' : 'Sparklet published.');
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            output.errorWithHint(`Failed to update sparklet: ${errorMessage}`);
            process.exit(1);
        }
    });

async function listSparklets(json = false): Promise<void> {
    try {
        const client = getApiClient();
        const response = await client.get('/sparklet/my');
        const sparklets: Sparklet[] = response.data.data || response.data.sparklets || response.data || [];

        if (json) {
            output.json(sparklets);
            return;
        }

        if (!Array.isArray(sparklets) || sparklets.length === 0) {
            output.dim('No sparklets found.');
            output.dim('Create sparklets by asking the agent to build a micro-app.');
            return;
        }

        output.header('Your Sparklets');

        for (const s of sparklets) {
            const published = s.isPublished
                ? output.colors.success('published')
                : output.colors.dim('draft');
            const id = output.colors.info(s.hashKey.substring(0, 8));
            const title = s.title || 'Untitled';
            const date = output.colors.dim(new Date(s.updatedAt).toLocaleDateString());

            output.listItem(`${id}  ${title}  ${published}  ${date}`);
        }

        output.newline();
        output.dim('Show: paean sparklet show <id> | Open: paean sparklet open <id>');
        output.newline();
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        output.errorWithHint(`Failed to list sparklets: ${errorMessage}`);
        process.exit(1);
    }
}
